import { zodResolver } from '@hookform/resolvers/zod';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import InfoIcon from '@mui/icons-material/Info';

import React, { useContext, useState } from 'react';

import { ResolverOptions, useForm } from 'react-hook-form';

import { z } from 'zod';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  DataProviderUpdateDTO,
  DataProviderV2UpdateSchema,
  DEFAULT_CAMPAIGN_NAME,
  isSupportedProvider,
  OUTBOUND_CAMPAIGN_NAME,
  PoolBrainDataProviderUpdateDTO,
  PoolBrainDataProviderUpdateSchema,
  ProviderVersions,
  ServiceTitanApiTestConnectionSchema,
  ServiceTitanPortalV2Company,
  ServiceTitanPortalV2DataProviderUpdateDTO,
  StCapacityDataProviderUpdateDTO,
  StCapacityDataProviderUpdateSchema,
  SupportedProviderEnum
} from '_lib/api/apiTypes.shim';

import { useQueryClient } from '_lib/api/QueryProvider';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { HeaderTextBlock } from '_lib/featureComponents/HeaderTextBlock/HeaderTextBlock';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';
import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { TabChangeObj } from '../../CompanyUpdate';

export type CompanyConfigDataProviderEditProps = {
  companyId: string;
  companyDataProvider: ServiceTitanPortalV2Company['dataProvider'] | null;
  onClose: () => void;
  tabChangeObj: TabChangeObj;
};

export const CompanyConfigDataProviderEdit = (props: CompanyConfigDataProviderEditProps) => {
  const { companyId, companyDataProvider, onClose, tabChangeObj } = props;

  const [validationErrors, setValidationErrors] = useState<string[]>();

  const [connectionVerified, setConnectionVerified] = useState(false);

  const { enqueueSnackbar } = useOurSnackbar();

  const queryClient = useQueryClient();

  const api = useContext(ApiContext);

  const companyQueryKey = api.companies.getByIdQueryKey({ id: companyId });

  const dataProviderPutMutation = api.companyDataProviders.usePut();
  const testConnectionPostMutation = api.companyDataProviders.useTestConnection();

  const dataProviderFormContext = useForm<DataProviderUpdateDTO>({
    resolver: (values, context, options) => {
      if (values.dataProvider.providerType === SupportedProviderEnum.ST_CAPACITY) {
        const schema = StCapacityDataProviderUpdateSchema;
        const resolver = zodResolver(schema);
        return resolver(
          values as StCapacityDataProviderUpdateDTO,
          context,
          options as ResolverOptions<StCapacityDataProviderUpdateDTO>
        );
      } else if (
        values.dataProvider.providerType === SupportedProviderEnum.SERVICE_TITAN &&
        'providerVersion' in values.dataProvider &&
        values.dataProvider.providerVersion === ProviderVersions.v2
      ) {
        const schema = DataProviderV2UpdateSchema;
        const resolver = zodResolver(schema);
        return resolver(
          values as ServiceTitanPortalV2DataProviderUpdateDTO,
          context,
          options as ResolverOptions<ServiceTitanPortalV2DataProviderUpdateDTO>
        );
      } else if (
        values.dataProvider.providerType === SupportedProviderEnum.POOL_BRAIN &&
        'providerVersion' in values.dataProvider &&
        values.dataProvider.providerVersion === ProviderVersions.v1
      ) {
        const schema = z.object({ dataProvider: PoolBrainDataProviderUpdateSchema });
        const resolver = zodResolver(schema);
        return resolver(
          values as PoolBrainDataProviderUpdateDTO,
          context,
          options as ResolverOptions<PoolBrainDataProviderUpdateDTO>
        );
      } else {
        return {
          errors: {
            root: { message: 'Unsupported data provider type' }
          },
          values
        };
      }
    },
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: async () => {
      return {
        dataProvider: {
          providerType: SupportedProviderEnum.SERVICE_TITAN,
          providerVersion: ProviderVersions.v2,
          providerOptions: {
            tenantId: companyDataProvider?.providerOptions.tenantId || '',
            portalName: companyDataProvider?.providerOptions.portalName || 'CallPoint',
            isEnterprise: companyDataProvider?.providerOptions.isEnterprise || false,
            useAdaptiveCapacity: companyDataProvider?.providerOptions.useAdaptiveCapacity || false
          }
        }
      } satisfies DataProviderUpdateDTO;
    }
  });

  const handleSave = apiSubmitHandler(
    dataProviderFormContext.handleSubmit,
    dataProviderFormContext.setError,
    async (data, { setFormErrors }) => {
      setValidationErrors(undefined);
      try {
        await dataProviderPutMutation.put({ id: companyId, data });
        enqueueSnackbar('Data Provider saved');
        queryClient.invalidateQueries({ queryKey: companyQueryKey });
        onClose();
      } catch (err) {
        if (err instanceof BadRequestError) {
          setValidationErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        logDebug('Data Provider form is invalid', { errors });
        setValidationErrors([JSON.stringify(errors, null, 4)]);
      }
    }
  );

  const testConnectionFormContext = useForm({
    resolver: zodResolver(ServiceTitanApiTestConnectionSchema),
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      dataProvider: {
        providerType: SupportedProviderEnum.SERVICE_TITAN,
        providerVersion: ProviderVersions.v2,
        providerOptions: { tenantId: '', clientId: '', clientSecret: '' }
      }
    }
  });
  const handleTestConnection = apiSubmitHandler(
    testConnectionFormContext.handleSubmit,
    testConnectionFormContext.setError,
    async (data, { setFormErrors }) => {
      setValidationErrors(undefined);
      try {
        await testConnectionPostMutation.create({ data: { ...data, companyId } });
        setConnectionVerified(true);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setValidationErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        logDebug('Data Provider form is invalid', { errors });
        setValidationErrors([JSON.stringify(errors, null, 4)]);
      }
    }
  );

  const { control, setValue, watch } = dataProviderFormContext;

  const providerType = watch('dataProvider.providerType');

  tabChangeObj.onSave = handleSave;
  tabChangeObj.isValid = dataProviderFormContext.formState.isValid;
  tabChangeObj.isDirty = dataProviderFormContext.formState.isDirty;

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        padding: 2
      })}>
      <BackdropSpinner open={!!dataProviderPutMutation.isPending || !!testConnectionPostMutation.isPending} />
      {!!validationErrors?.length && <ErrorDisplay errors={validationErrors} sx={{ borderRadius: 1 }} />}
      <FlexCard>
        <PrimaryCardHeader title='Data Provider Settings' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%', overflow: 'auto' }} spacing={1}>
          <SelectFormItem
            disabled={!!companyDataProvider}
            name='dataProvider.providerType'
            label='Provider'
            onChange={(e) => {
              if (isSupportedProvider(e.target.value)) {
                setValue(
                  'dataProvider.providerVersion',
                  e.target.value === SupportedProviderEnum.SERVICE_TITAN ? ProviderVersions.v2 : ProviderVersions.v1
                );

                if (
                  e.target.value === SupportedProviderEnum.SERVICE_TITAN ||
                  e.target.value === SupportedProviderEnum.ST_CAPACITY
                ) {
                  testConnectionFormContext.setValue('dataProvider.providerType', e.target.value);
                }
              }
            }}
            control={control}
            required
            fullWidth>
            {[
              SupportedProviderEnum.SERVICE_TITAN,
              SupportedProviderEnum.ST_CAPACITY,
              SupportedProviderEnum.POOL_BRAIN
            ].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </SelectFormItem>
          {providerType === SupportedProviderEnum.POOL_BRAIN && (
            <>
              <TextFieldFormItem
                disabled={!!companyDataProvider}
                control={control}
                required
                name='dataProvider.providerOptions.apiKey'
                label='Api Key'
                fullWidth
                slotProps={{
                  input: {
                    type: 'password'
                  }
                }}
              />
              <TextFieldFormItem
                disabled={!!companyDataProvider}
                control={control}
                required
                name='dataProvider.providerOptions.companyId'
                label='Company ID'
                fullWidth
              />
            </>
          )}
          {providerType === SupportedProviderEnum.SERVICE_TITAN && (
            <>
              <CheckboxFormItem
                control={control}
                label='Use Adaptive Capacity'
                name='dataProvider.providerOptions.useAdaptiveCapacity'
              />
              <CheckboxFormItem control={control} label='Enterprise' name='dataProvider.providerOptions.isEnterprise' />
              {!watch('dataProvider.providerOptions.isEnterprise') && (
                <TextFieldFormItem
                  control={control}
                  label='Portal Name'
                  name='dataProvider.providerOptions.portalName'
                  fullWidth
                  required
                />
              )}
            </>
          )}
          {(providerType === SupportedProviderEnum.SERVICE_TITAN ||
            providerType === SupportedProviderEnum.ST_CAPACITY) && (
            <>
              <TextFieldFormItem
                disabled={!!companyDataProvider || connectionVerified}
                control={control}
                required
                name='dataProvider.providerOptions.tenantId'
                label='Tenant ID'
                onChange={(e) => {
                  testConnectionFormContext.setValue('dataProvider.providerOptions.tenantId', e.target.value);
                }}
                fullWidth
              />
              {!companyDataProvider && (
                <>
                  <TextFieldFormItem
                    disabled={connectionVerified}
                    control={testConnectionFormContext.control}
                    required
                    name='dataProvider.providerOptions.clientId'
                    label='Client ID'
                    fullWidth
                    slotProps={{
                      input: {
                        type: 'password'
                      }
                    }}
                  />
                  <TextFieldFormItem
                    disabled={connectionVerified}
                    control={testConnectionFormContext.control}
                    required
                    name='dataProvider.providerOptions.clientSecret'
                    label='Client Secret'
                    fullWidth
                    slotProps={{
                      input: {
                        type: 'password'
                      }
                    }}
                  />
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Connection Status:
                    {connectionVerified ? (
                      <Tooltip title='Connection has been verified'>
                        <CloudDoneIcon fontSize='small' color='primary' />
                      </Tooltip>
                    ) : (
                      <Tooltip title='Connection requires verification'>
                        <CloudOffIcon fontSize='small' color='error' />
                      </Tooltip>
                    )}
                  </Typography>
                  <Stack useFlexGap direction='row' sx={{ width: '100%' }}>
                    <Button
                      variant='contained'
                      text='TEST'
                      disabled={connectionVerified}
                      onClick={handleTestConnection}
                    />
                    <Button
                      variant='contained'
                      color='cancel'
                      text='RESET'
                      disabled={!connectionVerified}
                      onClick={() => setConnectionVerified(false)}
                    />
                  </Stack>
                </>
              )}
            </>
          )}
          {providerType === SupportedProviderEnum.ST_CAPACITY && connectionVerified && (
            <>
              <Stack direction='row' spacing={0} useFlexGap sx={{ alignItems: 'center' }}>
                <CheckboxFormItem
                  control={control}
                  name='dataProvider.providerOptions.skillBasedAvailability'
                  label='Skill Based Availability'
                />
                <Tooltip title='Whether or not the "Skills" capacity enhancement is enabled for the company. To verify go to Service Titan > Company Settings > Capacity Planning > Configuration.'>
                  <InfoIcon fontSize='small' color='primary' />
                </Tooltip>
              </Stack>
              <HeaderTextBlock headerText='Default Campaign' text={DEFAULT_CAMPAIGN_NAME} />
              <HeaderTextBlock headerText='Outbound Campaign' text={OUTBOUND_CAMPAIGN_NAME} />
            </>
          )}
        </Stack>
        <Divider />
        <FlexBox sx={{ paddingX: 2, paddingY: 1, gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            text='Cancel'
            variant='contained'
            color='cancel'
            size='small'
            sx={{ textTransform: 'none' }}
          />
          <Button
            disabled={
              !companyDataProvider &&
              (providerType === SupportedProviderEnum.SERVICE_TITAN ||
                providerType === SupportedProviderEnum.ST_CAPACITY) &&
              !connectionVerified
            }
            onClick={handleSave}
            text='Save'
            variant='contained'
            size='small'
            sx={{ textTransform: 'none' }}
          />
        </FlexBox>
      </FlexCard>
    </FlexBox>
  );
};
